import React from "react";
import { FileText } from "lucide-react";
import useAsync from "../../utils/useAsync.js";
import { getQuarterly } from "../../utils/companyApi.js";
import { Loading } from "./common.jsx";

function fmt(v, kind, unit) {
  if (v === null || v === undefined || Number.isNaN(v)) return "-";
  if (kind === "pct") return `${Math.round(v)}%`;
  if (kind === "eps") return v.toFixed(2);
  const x = unit === "K" ? v * 1000 : unit === "B" ? v / 1000 : v;
  const d = unit === "B" ? 2 : 0;
  return x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

// Screener-style quarterly results with a row linking to each quarter's SEC report.
export default function QuarterlyTable({ ticker, count = 13, unit = "MM" }) {
  const { data, loading, error } = useAsync(() => getQuarterly(ticker, count), [ticker, count]);
  if (loading && !data) return <Loading />;
  if (error || !data || data.error) {
    return <p className="co-hint">No quarterly results for this company. It needs SEC financial statements.</p>;
  }
  const n = data.columns.length;
  const band = (i) => (Math.floor((n - 1 - i) / 4) % 2 === 0 ? "band" : "");
  const unitName = unit === "K" ? "thousands" : unit === "B" ? "billions" : "millions";

  return (
    <div>
      <div className="qt-wrap">
        <table className="qt">
          <thead>
            <tr>
              <th />
              {data.columns.map((c, i) => <th key={c.end} className={band(i)}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.key} className={r.strong ? "strong" : ""}>
                <td>{r.label}</td>
                {r.values.map((v, i) => <td key={i} className={band(i)}>{fmt(v, r.kind, unit)}</td>)}
              </tr>
            ))}
            <tr className="filing">
              <td>Filing</td>
              {data.filings.map((f, i) => (
                <td key={i} className={band(i)}>
                  {f ? (
                    <a href={f.url} target="_blank" rel="noreferrer" title={`${f.form}, filed ${f.filed}. Opens the original report on SEC EDGAR`}>
                      <FileText size={17} />
                    </a>
                  ) : "-"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="co-hint">
        US$ in {unitName} (EPS in $). From SEC 10-Q and 10-K filings; the fourth quarter is the full year minus the first nine months.
        The Filing row opens the original report on SEC EDGAR (the SEC publishes these as web documents, not PDFs; use your browser's Print, then Save as PDF).
      </p>
    </div>
  );
}